import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'commons/store/hooks';
import Nav from 'commons/wrappers/DashboardLayoutWrapper/components/Nav';
import {
    fetchDependencies,
    fetchSurvey,
    fetchSurveyElements,
    fetchSectionBySurvey,
    fetchSegmentsWithCounters,
    getSectionId,
    resetCollection,
    resetCurrentSection,
    resetDependencies,
    resetSurvey,
    resetElementDatas,
    selectCurrentSection,
    selectSectionId,
    selectSegments,
} from 'features/survey/redux/surveySlice';
import { fetchElements } from 'features/survey/redux/sectionSlice';
import { StyledSurveyLayout, StyledSurveyTitle } from './Form.styled';
import { FormContent } from './FormContent/FormContent';
import { LeftPanel } from './Menu/LeftPanel';
import { useSnackbar } from 'notistack';
import { ROUTES_SURVEY } from 'commons/constants/paths';
import { useTranslation } from "react-i18next";
import { SegmentsPanel } from "./Segment/SegmentsPanel";

export const Form: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id, section } = useParams<{ id: string, section: string }>();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    /**
     * State is holding props from routing redirect
     * @example: from dashboard, ae or query
     */
    const redirectSection = useAppSelector(selectSectionId);

    /** Current selected section data */
    const currentSection = useAppSelector(selectCurrentSection);

    /** Segments */
    const segments = useAppSelector(selectSegments);

    /** State is holding actual selected segment */
    const [selectedSegment, setSelectedSegment] = useState<any>({});

    /** State is holding actual selected section */
    const [selectedSection, setSelectedSection] = useState<any>({});

    /** Initial loading state status */
    const [initialLoading, setInitialLoading] = useState(true);

    /** Response pending status - if is true you can't change section */
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(t('loading-data') + '...')

    /**
     * Initial config - load first section of first segment
     */
    const initialConfig = async (segments: any) => {
        setIsDataLoading(true);

        if (!segments) return;
        setSelectedSegment(segments[0]);

        if (segments?.length === 0 || segments?.[0]?.sections?.length === 0) {
          setIsDataLoading(false);
          return;
        }

        if (segments[0]?.sections?.[0]?.id) {
            setSelectedSection(segments[0].sections[0].id);

            (id && !section) && navigate(ROUTES_SURVEY.DETAILS_EXTENDED(id, segments[0].sections[0].id));

            await loadSection(segments[0].sections[0].id, segments);
            await loadSectionElements(segments[0].sections[0].id);
        }

        setIsDataLoading(false);
    };

    /**
     * loadSectionElements()
     */
    const loadSectionElements = async (sectionId: any) => {
        await dispatch(fetchElements({ id: sectionId, surveyId: id }));
        setDataLoading(t('loading-field-dependencies') + '...');
        await dispatch(fetchDependencies(sectionId));
        setDataLoading(t('loading-field-data') + '...');
        await dispatch(fetchSurveyElements({ id, sectionId }));
    }

    /**
     * loadSection()
     * @todo one fn reload current section (elements, data, etc)
     */
    const loadSection = async (sectionId: any, segments: any = []) => {
        setSelectedSection(sectionId);
        setDataLoading(t('loading-section-data') + '...');

        // const section: any = await dispatch(fetchSection(sectionId)).unwrap();
        const _section: any = await dispatch(fetchSectionBySurvey({ id: sectionId, surveyId: id })).unwrap();

        if (id && sectionId) {
            !section && navigate(ROUTES_SURVEY.DETAILS_EXTENDED(id, sectionId), { replace: true });
            setSelectedSection(_section?.id);
        }

        if ((segments && segments?.length > 0) && _section?.segment?.id) {
            const segment = segments.find((segment: any) => segment?.id === _section?.segment?.id);
            if (segment) setSelectedSegment(segment);
        }
    };

    /**
     * loadSectionData()
     */
    const loadSectionData = async (sectionId: any, segments: any = []) => {
      if (isDataLoading) return;
        setIsDataLoading(true);

        try {
            await loadSection(sectionId, segments);
            await loadSectionElements(sectionId);
        } catch (error: any) {
            enqueueSnackbar(error?.detail || error?.message, { variant: 'error' });
        }

        /** clear redirect-data after redirect */
        dispatch(getSectionId({}));

        setIsDataLoading(false);
    };

    /** Config for redirect option */
    const redirectConfig = async (segments?: any) => {
        setIsDataLoading(true);

        if (redirectSection?.section) {
            await loadSection(redirectSection.section, segments);
            await loadSectionElements(redirectSection.section);
        }

        if (redirectSection?.element) {
            setIsOpenQueryPanel(redirectSection.element);
        }

        /** clear redirect-data after redirect */
        dispatch(getSectionId({}));

        setIsDataLoading(false);
    };

    /** Config for section from route params */
    const routeSectionConfig = async (sectionId: string, segments?: any) => {
        setIsDataLoading(true);

        try {
            await loadSection(sectionId, segments);
            await loadSectionElements(sectionId);
        } catch (e: any) {
            await initialConfig(segments);
        }

        setIsDataLoading(false);
    };

    /** Status of Queries Panel */
    const [isOpenQueryPanel, setIsOpenQueryPanel] = useState<null | string>(null);

    /**
     * Used to fetch patient data including ECRF data and segments
     */
    const fetchSurveyData = async () => {
        // init data survey
        setInitialLoading(true);

        setDataLoading(t('loading-segments') + '...')
        const segments = await dispatch(fetchSegmentsWithCounters(id)).unwrap();
        setDataLoading(t('loading-survey-data') + '...');
        const survey = id && await dispatch(fetchSurvey(id))?.unwrap();

        if (!survey) {
            return;
        }

        // init data section
        // incoming redirect data (AE, Query)
        if (redirectSection?.section !== undefined) {
            await redirectConfig(segments);
        } else if (section) {
            await routeSectionConfig(section, segments);
        // if exists current `section` in survey data
        } else if (survey?.currentSection?.id) {
            // await fetchSectionData(section || survey?.currentSection?.id, segments);
            await loadSectionData(survey?.currentSection?.id, segments);
        } else {
            await initialConfig(segments);
        }

        setInitialLoading(false);
    }

    useEffect(() => {
        fetchSurveyData().then();

        /** clear state after unmount */
        return () => {
            dispatch(resetSurvey());
            dispatch(resetCurrentSection());
            dispatch(resetElementDatas());
            dispatch(resetCollection());
            dispatch(resetDependencies());
        };
    }, [id]);

    useEffect(() => {
        if ((section && currentSection?.id) && section !== currentSection?.id) {
            loadSectionData(section, segments);
        }
    }, [section])

    return (
        <>
            <Nav />
            {!initialLoading && (
                <StyledSurveyLayout>
                    <LeftPanel
                        selectedSegment={selectedSegment}
                        selectedSection={selectedSection}
                        setSelectedSection={setSelectedSection}
                        isDataLoading={isDataLoading}
                        setIsDataLoading={setIsDataLoading}
                    />
                    <SegmentsPanel
                        selectedSegment={selectedSegment}
                        setSelectedSegment={setSelectedSegment}
                        isDataLoading={isDataLoading}
                    />
                    <FormContent
                        isDataLoading={isDataLoading}
                        setSelectedSegment={setSelectedSegment}
                        setIsDataLoading={setIsDataLoading}
                        isOpenQueryPanel={isOpenQueryPanel}
                        setIsOpenQueryPanel={setIsOpenQueryPanel}
                        updateSection={loadSectionData.bind(this)}
                    />
                </StyledSurveyLayout>
            )}

            {/** Initial view with loading animation */}
            {initialLoading && (
                <Box display="grid" style={{placeItems: 'center', height: '100vh'}}>
                    <Box display="grid" style={{placeItems: 'center', gap: '15px'}}>
                        <CircularProgress />
                        <StyledSurveyTitle>{dataLoading}</StyledSurveyTitle>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default Form;
